/* Benchmark VSL review page.
   A self-hosted player that can play a review split into two Shopify-hosted
   parts as one continuous video (Shopify Files caps hosted video at ten
   minutes), chapter seeking from the recap cards, a docked mini player that
   keeps the review playing while the reader scrolls, the end card, and the
   sticky buy bar. Falls back to a YouTube embed when only an id is given.
   Add to cart itself is the site's [data-bm-atc] contract in benchmark-pdp.js. */
(() => {
  'use strict';

  const fmt = (seconds) => {
    const s = Math.max(0, Math.floor(seconds || 0));
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, '0')}`;
  };

  const track = (action, extra) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: 'vsl_video', vsl_action: action }, extra || {}));
  };

  const initRoot = (root) => {
    const player = root.querySelector('[data-vsl-player]');
    if (!player) return;
    const slot = player.closest('[data-vsl-slot]') || player.parentElement;
    const q = (sel) => player.querySelector(sel);

    const poster = q('[data-vsl-poster]');
    const tap = q('[data-vsl-tap]');
    const endcard = q('[data-vsl-endcard]');
    const playBtn = q('[data-vsl-play]');
    const muteBtn = q('[data-vsl-mute]');
    const fsBtn = q('[data-vsl-fs]');
    const closeBtn = q('[data-vsl-close]');
    const replayBtn = q('[data-vsl-replay]');
    const bar = q('[data-vsl-bar]');
    const fill = q('[data-vsl-fill]');
    const knob = q('[data-vsl-knob]');
    const ticks = q('[data-vsl-ticks]');
    const timeEl = q('[data-vsl-time]');
    const ghost = slot.querySelector('[data-vsl-return]');

    const vids = [...player.querySelectorAll('video[data-vsl-part]')];
    const ytHost = q('[data-vsl-yt]');
    const total = parseFloat(player.dataset.vslDuration) || 0;
    const durations = vids.map((v, i) => parseFloat(v.dataset.vslSeconds) || (i === 0 ? total : 0));
    const chapters = [...root.querySelectorAll('[data-vsl-seek]')];

    let active = 0;
    let started = false;
    let dragging = false;
    let dockDismissed = false;
    let slotVisible = true;
    let controlsTimer = null;
    let yt = null;
    // the global time a seek is heading for, while the element still reports
    // the old position: a second seek issued during the first must build on it
    let pending = null;
    const milestones = new Set();

    /* ---------- sources: pick one per part on first play, never before ---------- */
    const sourcesFor = (v) => {
      try {
        return JSON.parse(v.querySelector('script[data-vsl-sources]')?.textContent || '[]');
      } catch (e) {
        return [];
      }
    };
    const pickSource = (list) => {
      if (!list.length) return null;
      const hls = list.find((s) => /mpegurl/i.test(s.t || ''));
      if (hls && vids[0].canPlayType('application/vnd.apple.mpegurl')) return hls;
      // a phone should not pull the 1080 rendition when a 720 exists
      const cap = window.innerWidth < 750 ? 720 : 1080;
      const mp4s = list.filter((s) => !/mpegurl/i.test(s.t || '')).sort((a, b) => (b.h || 0) - (a.h || 0));
      return mp4s.find((s) => !s.h || s.h <= cap) || mp4s[mp4s.length - 1] || null;
    };
    const ensureSrc = (v) => {
      if (v.dataset.vslLoaded) return;
      const src = pickSource(sourcesFor(v));
      if (!src) return;
      v.dataset.vslLoaded = '1';
      v.preload = 'auto';
      v.src = src.u;
      v.load();
    };

    /* ---------- time across the parts ---------- */
    const sumDur = () => durations.reduce((a, b) => a + b, 0) || total;
    const globalTime = () => {
      // a part that has not loaded yet reports 0 and is not "seeking", so the
      // requested target stands in until a seek actually completes
      if (pending !== null) return pending;
      return active === 0 ? vids[0].currentTime : durations[0] + vids[active].currentTime;
    };
    const isPlaying = () => {
      if (yt) return typeof yt.getPlayerState === 'function' && yt.getPlayerState() === 1;
      return started && vids.length > 0 && !vids[active].paused && !vids[active].ended;
    };

    const render = (override) => {
      const d = sumDur();
      const t = typeof override === 'number' ? override : vids.length ? globalTime() : 0;
      const r = d ? Math.min(1, Math.max(0, t / d)) : 0;
      if (fill) fill.style.width = `${r * 100}%`;
      if (knob) knob.style.left = `${r * 100}%`;
      if (timeEl) timeEl.textContent = `${fmt(t)} / ${fmt(d)}`;
      if (bar) bar.setAttribute('aria-valuenow', String(Math.round(t)));
    };

    const layoutTicks = () => {
      if (!ticks) return;
      const d = sumDur();
      ticks.innerHTML = '';
      if (!d) return;
      chapters.forEach((btn) => {
        const t = parseFloat(btn.dataset.vslSeek) || 0;
        if (t <= 0 || t >= d) return;
        const i = document.createElement('i');
        i.style.left = `${(t / d) * 100}%`;
        ticks.appendChild(i);
      });
    };

    const hideEnd = () => {
      if (endcard) endcard.hidden = true;
      player.classList.remove('is-ended');
    };

    const setActive = (i) => {
      if (i === active) return;
      vids[active].pause();
      vids[active].classList.remove('is-active');
      active = i;
      vids[active].classList.add('is-active');
    };

    const seekTo = (t, thenPlay) => {
      if (!vids.length) return;
      const d = sumDur();
      t = Math.min(Math.max(0, t), Math.max(0, d - 0.25));
      const i = vids.length > 1 && t >= durations[0] ? 1 : 0;
      const local = i === 1 ? t - durations[0] : t;
      setActive(i);
      const v = vids[i];
      ensureSrc(v);
      pending = t;
      const apply = () => {
        try {
          v.currentTime = local;
        } catch (e) {
          /* a part without metadata yet queues the seek itself */
        }
        if (thenPlay) v.play().catch(() => {});
      };
      if (v.readyState >= 1) apply();
      else v.addEventListener('loadedmetadata', apply, { once: true });
      hideEnd();
      render(t);
    };

    const showControls = () => {
      player.classList.add('show-controls');
      clearTimeout(controlsTimer);
      controlsTimer = setTimeout(() => player.classList.remove('show-controls'), 2600);
    };

    const start = (at) => {
      started = true;
      player.classList.add('is-started');
      vids.forEach((v) => {
        v.muted = false;
      });
      player.classList.remove('is-muted');
      track('play', { vsl_start_at: Math.round(at || 0) });
      seekTo(at || 0, true);
      showControls();
    };

    const toggle = () => {
      if (!started) return start(0);
      const v = vids[active];
      if (v.paused || v.ended) v.play().catch(() => {});
      else v.pause();
      showControls();
    };

    const finish = () => {
      player.classList.add('is-ended');
      player.classList.remove('is-playing');
      if (endcard) endcard.hidden = false;
      if (!milestones.has(1)) {
        milestones.add(1);
        track('complete');
      }
    };

    /* ---------- docking ---------- */
    let dockSuppressed = false;
    let dockSuppressTimer = null;
    const dock = (on) => {
      player.classList.toggle('is-docked', on);
      slot.classList.toggle('is-vacated', on);
    };

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        ([entry]) => {
          slotVisible = entry.isIntersecting;
          if (entry.isIntersecting) dock(false);
          else if (isPlaying() && !dockDismissed && !dockSuppressed) dock(true);
        },
        { threshold: 0.3 }
      ).observe(slot);
    }

    closeBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (yt) yt.pauseVideo?.();
      else vids[active]?.pause();
      dockDismissed = true;
      dock(false);
    });

    const returnToSlot = () => {
      dockDismissed = false;
      dock(false);
      // a smooth scroll leaves the slot out of view for a few frames, and the
      // observer below would dock the player again mid-flight
      dockSuppressed = true;
      clearTimeout(dockSuppressTimer);
      dockSuppressTimer = setTimeout(() => {
        dockSuppressed = false;
      }, 1200);
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      slot.scrollIntoView({ behavior: reduce ? 'instant' : 'smooth', block: 'center' });
    };

    ghost?.addEventListener('click', returnToSlot);

    // The corner preview is one target. A tap brings the visitor back up to the
    // player (the mini-player convention); the x pauses and dismisses. Bound
    // here, not only on the self-hosted path, because a docked YouTube frame
    // otherwise exposes its own chrome at thumbnail size: the title link and
    // the logo open youtube.com, and a tap on the picture pauses into YouTube's
    // "Watch on YouTube" overlay.
    tap?.addEventListener('click', () => {
      if (player.classList.contains('is-docked')) return returnToSlot();
      if (vids.length) toggle();
    });

    /* ---------- YouTube ----------
       The plays that count as views on the channel happen inside YouTube's
       player, so when an id is set it is the primary source, not a fallback.
       'native' shows YouTube's own thumbnail and play button from the start
       (one tap, and the tap is the counted view); 'poster' keeps our art and
       swaps in the player on tap, so the visitor taps YouTube's button next. */
    let ytSeek = null;
    if (ytHost && !vids.length) {
      const id = ytHost.dataset.vslYt;
      const native = player.dataset.vslYtDisplay === 'native';
      let ytPoll = null;
      let ytPlayTracked = false;
      // YouTube only counts a view when playback starts from ITS play button
      // (IFrame API reference, playVideo(): "A playback only counts toward a
      // video's official view count if it is initiated via a native play
      // button"), and browsers refuse a programmatic sound-on play inside the
      // iframe anyway until it has been tapped once. So until the first native
      // play, a chapter tap only cues the video at that moment and brings the
      // player to the reader; the red button starts it there and the view
      // counts. After that, seeks and plays go through the API.
      let ytHasPlayed = false;
      // native mode builds the player at load; commands sent before its
      // ready signal are dropped by the API, so seeks wait on this
      let ytReady = null;
      const loadApi = () =>
        new Promise((resolve) => {
          if (window.YT?.Player) return resolve();
          const prev = window.onYouTubeIframeAPIReady;
          window.onYouTubeIframeAPIReady = () => {
            if (typeof prev === 'function') prev();
            resolve();
          };
          const s = document.createElement('script');
          s.src = 'https://www.youtube.com/iframe_api';
          document.head.appendChild(s);
        });
      const ytTime = () => {
        try {
          const d = yt.getDuration();
          const t = yt.getCurrentTime();
          return d ? t / d : 0;
        } catch (e) {
          return 0;
        }
      };
      const pollMilestones = () => {
        const pct = ytTime();
        [0.25, 0.5, 0.75].forEach((m) => {
          if (pct >= m && !milestones.has(m)) {
            milestones.add(m);
            track(`progress_${Math.round(m * 100)}`);
          }
        });
      };
      const onState = (e) => {
        const playing = e.data === 1;
        player.classList.toggle('is-playing', playing);
        player.classList.toggle('is-paused', !playing);
        if (playing) {
          started = true;
          ytHasPlayed = true;
          player.classList.add('is-started');
          hideEnd();
          if (!ytPlayTracked) {
            ytPlayTracked = true;
            track('play', { vsl_start_at: Math.round((() => { try { return yt.getCurrentTime(); } catch (err) { return 0; } })()) });
          }
          if (slotVisible) dockDismissed = false;
          clearInterval(ytPoll);
          ytPoll = setInterval(pollMilestones, 1000);
        } else {
          clearInterval(ytPoll);
        }
        if (e.data === 0) finish();
      };
      const create = (at, autoplay) =>
        new Promise((resolve) => {
          yt = new window.YT.Player(ytHost, {
            videoId: id,
            playerVars: {
              autoplay: autoplay ? 1 : 0,
              rel: 0,
              playsinline: 1,
              iv_load_policy: 3,
              start: Math.floor(at || 0),
              origin: window.location.origin,
            },
            events: {
              onReady: () => resolve(),
              onStateChange: onState,
              // 101/150: the owner disallowed embedding, 100: removed, 2: bad id
              onError: (e) => {
                player.classList.add('is-yt-error');
                track('error', { vsl_error: e.data });
              },
            },
          });
        });
      const startYt = async (at, andPlay) => {
        player.classList.add('is-started', 'is-yt');
        hideEnd();
        if (ytReady) await ytReady;
        if (!yt) {
          // poster mode: the player appears cued at this moment; the visitor
          // presses YouTube's button (an autoplay here would not count)
          await loadApi();
          await create(at, andPlay === true);
          return;
        }
        if (!ytHasPlayed) {
          // a chapter tap asks to watch that moment, so it loads and plays.
          // loadVideoById, not cueVideoById followed by playVideo: the cue is
          // still loading when the play call lands and the player swallows it.
          // The poster tap and the ?t= deep link do not pass andPlay, so their
          // first play stays on YouTube's own button, where the view counts.
          if (andPlay) yt.loadVideoById({ videoId: id, startSeconds: Math.floor(at || 0) });
          else yt.cueVideoById({ videoId: id, startSeconds: Math.floor(at || 0) });
          return;
        }
        yt.seekTo(at || 0, true);
        yt.playVideo();
      };
      ytSeek = startYt;
      poster?.addEventListener('click', () => startYt(0));
      replayBtn?.addEventListener('click', () => startYt(0));
      if (native) {
        // the player is the poster: build it as soon as the API is here
        ytReady = loadApi().then(() => create(0, false));
      }
    }

    /* ---------- self-hosted parts ---------- */
    vids.forEach((v, i) => {
      v.addEventListener('loadedmetadata', () => {
        if (v.duration && Number.isFinite(v.duration)) durations[i] = v.duration;
        render();
        layoutTicks();
      });
      v.addEventListener('timeupdate', () => {
        if (v !== vids[active]) return;
        if (!dragging) render();
        // warm the next part while this one still has half a minute to run
        const next = vids[i + 1];
        if (next && !next.dataset.vslLoaded && durations[i] - v.currentTime < 30) ensureSrc(next);
        const d = sumDur();
        if (!d) return;
        const pct = globalTime() / d;
        [0.25, 0.5, 0.75].forEach((m) => {
          if (pct >= m && !milestones.has(m)) {
            milestones.add(m);
            track(`progress_${Math.round(m * 100)}`);
          }
        });
      });
      v.addEventListener('seeked', () => {
        // a seek issued while this one was in flight keeps the target alive
        if (v === vids[active] && !v.seeking) pending = null;
      });
      v.addEventListener('playing', () => {
        if (v === vids[active] && !v.seeking) pending = null;
      });
      v.addEventListener('play', () => {
        if (v !== vids[active]) return;
        player.classList.add('is-playing');
        player.classList.remove('is-paused');
        hideEnd();
        if (slotVisible) dockDismissed = false;
      });
      v.addEventListener('pause', () => {
        if (v !== vids[active]) return;
        player.classList.remove('is-playing');
        player.classList.add('is-paused');
      });
      v.addEventListener('ended', () => {
        if (v !== vids[active]) return;
        if (vids[i + 1]) seekTo(durations.slice(0, i + 1).reduce((a, b) => a + b, 0), true);
        else finish();
      });
    });

    if (vids.length) {
      poster?.addEventListener('click', () => start(0));
      replayBtn?.addEventListener('click', () => seekTo(0, true));
      playBtn?.addEventListener('click', toggle);

      muteBtn?.addEventListener('click', () => {
        const muted = !vids[active].muted;
        vids.forEach((v) => {
          v.muted = muted;
        });
        player.classList.toggle('is-muted', muted);
        showControls();
      });

      fsBtn?.addEventListener('click', () => {
        if (document.fullscreenElement) {
          document.exitFullscreen?.();
          return;
        }
        if (player.requestFullscreen) player.requestFullscreen().catch(() => {});
        else if (vids[active].webkitEnterFullscreen) vids[active].webkitEnterFullscreen();
      });

      if (bar) {
        const ratioFrom = (e) => {
          const r = bar.getBoundingClientRect();
          return Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
        };
        bar.addEventListener('pointerdown', (e) => {
          e.preventDefault();
          dragging = true;
          bar.setPointerCapture?.(e.pointerId);
          render(ratioFrom(e) * sumDur());
        });
        bar.addEventListener('pointermove', (e) => {
          if (dragging) render(ratioFrom(e) * sumDur());
        });
        const release = (e) => {
          if (!dragging) return;
          dragging = false;
          const t = ratioFrom(e) * sumDur();
          if (!started) start(t);
          else seekTo(t, true);
        };
        bar.addEventListener('pointerup', release);
        bar.addEventListener('pointercancel', () => {
          dragging = false;
          render();
        });
        bar.addEventListener('keydown', (e) => {
          const step = e.shiftKey ? 30 : 5;
          if (e.key === 'ArrowRight') seekTo(globalTime() + step, isPlaying());
          else if (e.key === 'ArrowLeft') seekTo(globalTime() - step, isPlaying());
          else return;
          e.preventDefault();
          showControls();
        });
      }

      player.addEventListener('keydown', (e) => {
        if (e.target !== player) return;
        if (e.key === ' ' || e.key.toLowerCase() === 'k') toggle();
        else if (e.key.toLowerCase() === 'm') muteBtn?.click();
        else if (e.key.toLowerCase() === 'f') fsBtn?.click();
        else if (e.key === 'ArrowRight') seekTo(globalTime() + 5, isPlaying());
        else if (e.key === 'ArrowLeft') seekTo(globalTime() - 5, isPlaying());
        else return;
        e.preventDefault();
      });

      player.addEventListener('pointermove', () => {
        if (started) showControls();
      });

      render();
      layoutTicks();
    }

    /* ---------- chapters ---------- */
    chapters.forEach((btn) => {
      btn.addEventListener('click', () => {
        const t = parseFloat(btn.dataset.vslSeek) || 0;
        track('chapter', { vsl_chapter: btn.dataset.vslChapter || fmt(t) });
        // a chapter tap is a request to watch that moment: take the reader up
        // to the player and start there, rather than docking a thumbnail in
        // the corner (Paul, Sep 8)
        returnToSlot();
        if (ytSeek) ytSeek(t, true);
        else if (!started) start(t);
        else seekTo(t, true);
      });
    });

    /* ---------- deep links: ?t=75, ?t=1m15s, ?t=1:15 or #t=75 ----------
       The Clip nodes in the page's VideoObject point Google's key moments at
       these URLs, and each one must land the visitor at that moment. Same
       path as a chapter tap, minus the chapter event: YouTube cues at the
       offset (a view only counts from its own button, so no autoplay), the
       self-hosted parts seek without playing, and the player scrolls into view. */
    const parseStart = (raw) => {
      if (!raw) return 0;
      const v = String(raw).trim().toLowerCase();
      let m = v.match(/^(?:(\d+)m)?(?:(\d+)s?)?$/);
      if (m && (m[1] || m[2])) return (parseInt(m[1] || '0', 10) * 60) + parseInt(m[2] || '0', 10);
      m = v.match(/^(\d+):(\d{1,2})$/);
      if (m) return (parseInt(m[1], 10) * 60) + parseInt(m[2], 10);
      return 0;
    };
    const startAt = (() => {
      try {
        const q = new URLSearchParams(window.location.search).get('t');
        const h = (window.location.hash.match(/[#&]t=([^&]+)/) || [])[1];
        const t = parseStart(q || h);
        return t > 0 && (!total || t < total) ? t : 0;
      } catch (err) { return 0; }
    })();
    if (startAt) {
      const go = () => {
        if (ytSeek) ytSeek(startAt);
        else if (vids.length) seekTo(startAt, false);
        slot.scrollIntoView({ behavior: 'instant', block: 'center' });
      };
      // the YouTube player is created on the API's own schedule; cue once it exists
      if (ytHost && !vids.length) setTimeout(go, 800);
      else go();
    }

    /* ---------- sticky buy bars: the PDP's, shown past the hero button ---------- */
    const stickyHost = root.querySelector('[data-vsl-sticky]');
    const heroCta = root.querySelector('[data-vsl-hero-cta]');
    if (stickyHost && heroCta && 'IntersectionObserver' in window) {
      const stickyAtc = stickyHost.querySelector('[data-sticky-atc]');
      const stickyBar = stickyHost.querySelector('[data-sticky-bar]');
      // same retreat as benchmark-pdp.js: off while the footer's last row shows
      const footerEnd = document.querySelector('.footer-benchmark__bottom') || document.querySelector('.footer-benchmark');
      let pastHero = false;
      let atFooter = false;
      const apply = () => {
        const on = pastHero && !atFooter;
        stickyAtc?.classList.toggle('show', on);
        stickyBar?.classList.toggle('show', on);
        document.documentElement.classList.toggle('vsl-sticky-on', on);
      };
      new IntersectionObserver(([entry]) => {
        pastHero = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        apply();
      }).observe(heroCta);
      if (footerEnd) {
        new IntersectionObserver(([entry]) => {
          atFooter = entry.isIntersecting;
          apply();
        }).observe(footerEnd);
      }
    }

    root.querySelectorAll('[data-vsl-cta]').forEach((btn) => {
      btn.addEventListener('click', () => track('cta', { vsl_cta: btn.dataset.vslCta }));
    });
  };

  const boot = () => document.querySelectorAll('[data-vsl]').forEach(initRoot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
